import { PERCENTAGE_LIST } from "constants/haulpay-broker";
import { CARRIER_PAYMENT_SPEED } from "constants/haulpay-carrier";
import type { IOption } from "models/Form";

export type PayoutDaysValue = number | string | null;

export type PaymentSettingsValue = {
  payout_days?: PayoutDaysValue;
  factoring_fee_split_rate?: number | string | null;
};

export type EditablePaymentSettingsValue = {
  carrierPaymentSpeed?: IOption | null;
  brokersQRFee?: IOption | null;
};

export type ConfirmedPaymentSettingsValue = {
  carrierPaymentSpeed: IOption;
  brokersQRFee: IOption;
};

export const LOCKED_BROKER_SPLIT_OPTION = PERCENTAGE_LIST[2] as IOption;

const getPaymentSpeedValue = (value?: PayoutDaysValue): string | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  if (typeof value === "string" && value.startsWith("ach_")) return value;
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return undefined;
  const normalizedDays = numericValue === 0 ? 1 : numericValue;
  return `ach_${normalizedDays}_day`;
};

const ACH_DAY_PATTERN = /ach_(\d+)_day/;

export const getPaymentSpeedSummary = (value?: PayoutDaysValue): string => {
  const normalizedValue = getPaymentSpeedValue(value);
  const dayMatch = ACH_DAY_PATTERN.exec(normalizedValue ?? "");
  const days = dayMatch?.[1] ? Number(dayMatch[1]) : Number(value);
  return days === 1 ? "1 Day" : `${days} Days`;
};

const getCarrierPaymentSpeedOption = (
  value?: EditablePaymentSettingsValue["carrierPaymentSpeed"],
  fallbackPayoutDays?: PaymentSettingsValue["payout_days"]
) => {
  if (value) return value;

  if (
    fallbackPayoutDays === null ||
    fallbackPayoutDays === undefined ||
    fallbackPayoutDays === ""
  ) {
    return undefined;
  }

  const numericPayoutDays = Number(fallbackPayoutDays);
  if (!Number.isNaN(numericPayoutDays)) {
    const normalizedPayoutDays = numericPayoutDays === 0 ? 1 : numericPayoutDays;
    return CARRIER_PAYMENT_SPEED.find(({ day }) => day === normalizedPayoutDays);
  }

  return CARRIER_PAYMENT_SPEED.find(({ value: optionValue }) => optionValue === fallbackPayoutDays);
};

const normalizeConfirmedPaymentSettings = ({
  value,
  initialValue,
  currentValue,
  defaultValue,
  hasFactoringCompany
}: {
  value: EditablePaymentSettingsValue;
  initialValue?: EditablePaymentSettingsValue;
  currentValue?: PaymentSettingsValue;
  defaultValue?: PaymentSettingsValue;
  hasFactoringCompany: boolean;
}): ConfirmedPaymentSettingsValue | null => {
  const carrierPaymentSpeed =
    getCarrierPaymentSpeedOption(value.carrierPaymentSpeed) ??
    getCarrierPaymentSpeedOption(initialValue?.carrierPaymentSpeed) ??
    getCarrierPaymentSpeedOption(undefined, currentValue?.payout_days) ??
    getCarrierPaymentSpeedOption(undefined, defaultValue?.payout_days);

  if (!carrierPaymentSpeed) return null;

  return {
    carrierPaymentSpeed,
    brokersQRFee: hasFactoringCompany
      ? LOCKED_BROKER_SPLIT_OPTION
      : value.brokersQRFee ?? initialValue?.brokersQRFee ?? (PERCENTAGE_LIST[0] as IOption)
  };
};

interface ConfirmContractTypeChangePaymentSettingsArgs {
  openModal: (modalId: string, props: Record<string, unknown>) => void;
  closeModal?: (modalId: string) => void;
  currentValue?: PaymentSettingsValue;
  defaultValue?: PaymentSettingsValue;
  initialValue?: EditablePaymentSettingsValue;
  hasFactoringCompany?: boolean;
  factoringId?: string;
  fromContractType?: string;
  toContractType?: string;
  signal?: AbortSignal;
}

const CONTRACT_TYPE_CHANGE_CONFIRMATION_MODAL_ID = "contract-type-change-confirmation";

const createAbortError = () => {
  const error = new Error("The operation was aborted.");
  error.name = "AbortError";

  return error;
};

export const confirmContractTypeChangePaymentSettings = ({
  openModal,
  closeModal,
  currentValue,
  defaultValue,
  initialValue,
  hasFactoringCompany = false,
  factoringId,
  fromContractType,
  toContractType,
  signal
}: ConfirmContractTypeChangePaymentSettingsArgs) =>
  new Promise<ConfirmedPaymentSettingsValue>((resolve, reject) => {
    if (signal?.aborted) {
      reject(createAbortError());
      return;
    }

    let isSettled = false;

    const cleanupAbortListener = () => {
      if (signal && handleAbort) {
        signal.removeEventListener("abort", handleAbort);
      }
    };

    const settleResolve = (value: ConfirmedPaymentSettingsValue) => {
      if (isSettled) return;

      isSettled = true;
      cleanupAbortListener();
      resolve(value);
    };

    const settleReject = (reason: unknown) => {
      if (isSettled) return;

      isSettled = true;
      cleanupAbortListener();
      reject(reason);
    };

    const handleAbort = () => {
      closeModal?.(CONTRACT_TYPE_CHANGE_CONFIRMATION_MODAL_ID);
      settleReject(createAbortError());
    };

    signal?.addEventListener("abort", handleAbort, { once: true });

    openModal(CONTRACT_TYPE_CHANGE_CONFIRMATION_MODAL_ID, {
      currentValue,
      defaultValue,
      initialValue,
      hasFactoringCompany,
      factoringId,
      fromContractType,
      toContractType,
      onConfirm: (value: EditablePaymentSettingsValue) => {
        if (isSettled || signal?.aborted) {
          return;
        }

        const normalizedValue = normalizeConfirmedPaymentSettings({
          value,
          initialValue,
          currentValue,
          defaultValue,
          hasFactoringCompany
        });

        if (!normalizedValue) {
          settleReject("cancel-edit-fr");
          return;
        }

        settleResolve(normalizedValue);
      },
      onCloseModal: () => {
        if (isSettled || signal?.aborted) {
          return;
        }

        settleReject("cancel-edit-fr");
      }
    });
  });

export interface OffPlatformEditSummaryRow {
  label: string;
  from?: string;
  to?: string;
  value?: string;
}

interface ConfirmSelfFinanceOffPlatformEditArgs {
  openModal: (modalId: string, props: Record<string, unknown>) => void;
  closeModal?: (modalId: string) => void;
  changes: OffPlatformEditSummaryRow[];
  recomputed: OffPlatformEditSummaryRow[];
  amountChanged: boolean;
  payoutDaysChanged?: boolean;
  signal?: AbortSignal;
}

const SELF_FINANCE_OFF_PLATFORM_EDIT_MODAL_ID = "self-finance-off-platform-edit";

export const confirmSelfFinanceOffPlatformEdit = ({
  openModal,
  closeModal,
  changes,
  recomputed,
  amountChanged,
  payoutDaysChanged,
  signal
}: ConfirmSelfFinanceOffPlatformEditArgs) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(createAbortError());
      return;
    }

    let isSettled = false;

    const cleanupAbortListener = () => {
      if (signal && handleAbort) {
        signal.removeEventListener("abort", handleAbort);
      }
    };

    const settleResolve = () => {
      if (isSettled) return;

      isSettled = true;
      cleanupAbortListener();
      resolve();
    };

    const settleReject = (reason: unknown) => {
      if (isSettled) return;

      isSettled = true;
      cleanupAbortListener();
      reject(reason);
    };

    const handleAbort = () => {
      closeModal?.(SELF_FINANCE_OFF_PLATFORM_EDIT_MODAL_ID);
      settleReject(createAbortError());
    };

    signal?.addEventListener("abort", handleAbort, { once: true });

    openModal(SELF_FINANCE_OFF_PLATFORM_EDIT_MODAL_ID, {
      changes,
      recomputed,
      amountChanged,
      payoutDaysChanged,
      onConfirm: () => {
        if (isSettled || signal?.aborted) {
          return;
        }

        settleResolve();
      },
      onCloseModal: () => {
        if (isSettled || signal?.aborted) {
          return;
        }

        settleReject("cancel-edit-fr");
      }
    });
  });
